/**
 * Page 2 of Form SS-4 — the "Do I Need an EIN?" reference table.
 *
 * Static: nothing on this page is filled in. Rules come from ss4-rules.ts;
 * the table's row boundaries are the H_RULES_P2 entries between y=120 and
 * y=528, and its two column splits are the only verticals on the page — the
 * official draws no outer left/right border around the table.
 *
 * The official's own sheet is 828pt tall, but every mark sits above y=700, so
 * the replica renders it on a Letter sheet at the same top-anchored
 * coordinates and the visible result is identical.
 */
import { At, Rules, pt } from "./ss4-layout";
import { H_RULES_P2, V_RULES_P2 } from "./ss4-rules";

/** Column text insets, measured: col 1 sits 1pt off the page margin, cols 2-3 8.5pt off their rule. */
const C1 = 37;
const C2 = 159.5;
const C3 = 368.5;

/** Footnote reference mark. */
const Sup = ({ n }: { n: number }) => (
  <span style={{ fontSize: pt(5), verticalAlign: "super", lineHeight: 0 }}>
    {n}
  </span>
);

/** [rowTop, applicant, and, then] — rowTop is the measured top rule of the row. */
const ROWS: Array<[number, React.ReactNode, React.ReactNode, React.ReactNode]> = [
  [
    132,
    "started a new business",
    <>
      doesn’t currently have (nor expect to have)
      <br />
      employees
    </>,
    <>
      complete lines 1, 2, 4a–8a, 8b–c (if applicable), 9a,
      <br />
      9b (if applicable), 10–14, and 16–18.
    </>,
  ],
  [
    156,
    <>
      hired (or will hire) employees,
      <br />
      including household employees
    </>,
    "doesn’t already have an EIN",
    <>
      complete lines 1, 2, 4a–6, 7a–b, 8a,
      <br />
      8b–c (if applicable), 9a, 9b (if applicable), and 10–18.
    </>,
  ],
  [
    180,
    "opened a bank account",
    "needs an EIN for banking purposes only",
    <>
      complete lines 1–5b, 7a–b, 8a, 8b–c
      <br />
      (if applicable), 9a, 9b (if applicable), 10, and 18.
    </>,
  ],
  [
    204,
    "changed type of organization",
    <>
      either the legal character of the organization or its
      <br />
      ownership changed (for example, you incorporate a
      <br />
      sole proprietorship or form a partnership)
      <Sup n={2} />
    </>,
    "complete lines 1–18 (as applicable).",
  ],
  [
    240,
    <>
      purchased a going business
      <Sup n={3} />
    </>,
    "doesn’t already have an EIN",
    "complete lines 1–18 (as applicable).",
  ],
  [
    252,
    "created a trust",
    <>
      the trust is other than a grantor trust or an IRA
      <br />
      trust
      <Sup n={4} />
    </>,
    "complete lines 1–18 (as applicable).",
  ],
  [
    276,
    <>
      created a pension plan as a
      <br />
      plan administrator
      <Sup n={5} />
    </>,
    "needs an EIN for reporting purposes",
    "complete lines 1, 3, 4a–5b, 7a–b, 9a, 10, and 18.",
  ],
  [
    300,
    <>
      is a foreign person needing an
      <br />
      EIN to comply with IRS
      <br />
      withholding regulations
    </>,
    <>
      needs an EIN to complete a Form W-8 (other than
      <br />
      Form W-8ECI), avoid withholding on portfolio assets,
      <br />
      or claim tax treaty benefits
      <Sup n={6} />
    </>,
    <>
      complete lines 1–5b, 7a–b (SSN or ITIN as applicable),
      <br />
      8a, 8b–c (if applicable), 9a, 9b (if applicable), 10,
      <br />
      and 18.
    </>,
  ],
  [
    336,
    "is administering an estate",
    "needs an EIN to report estate income on Form 1041",
    <>
      complete lines 1–7b, 9a, 10–12, 13–17 (if applicable),
      <br />
      and 18.
    </>,
  ],
  [
    360,
    <>
      is a withholding agent for
      <br />
      taxes on nonwage income
      <br />
      paid to an alien (that is,
      <br />
      individual, corporation, or
      <br />
      partnership, etc.)
    </>,
    <>
      is an agent, broker, fiduciary, manager, tenant, or
      <br />
      spouse who is required to file Form 1042, Annual
      <br />
      Withholding Tax Return for U.S. Source Income of
      <br />
      Foreign Persons
    </>,
    <>
      complete lines 1, 2, 3 (if applicable), 4a–5b, 7a–b, 8a,
      <br />
      8b–c (if applicable), 9a, 9b (if applicable), 10, and 18.
    </>,
  ],
  [
    408,
    "is a state or local agency",
    <>
      serves as a tax reporting agent for public assistance
      <br />
      recipients under Rev. Proc. 80-4, 1980-1 C.B. 581
      <Sup n={7} />
    </>,
    "complete lines 1, 2, 4a–5b, 7a–b, 9a, 10, and 18.",
  ],
  [
    432,
    <>
      is a single-member LLC (or
      <br />
      similar single-member entity)
    </>,
    <>
      needs an EIN to file Form 8832, Entity Classification
      <br />
      Election, for filing employment tax returns and excise
      <br />
      tax returns, or for state reporting purposes
      <Sup n={8} />, or is a
      <br />
      foreign-owned U.S. disregarded entity and needs an
      <br />
      EIN to file Form 5472, Information Return of a 25%
      <br />
      Foreign-Owned U.S. Corporation or a Foreign
      <br />
      Corporation Engaged in a U.S. Trade or Business
    </>,
    "complete lines 1–18 (as applicable).",
  ],
  [
    504,
    "is an S corporation",
    <>
      needs an EIN to file Form 2553, Election by a Small
      <br />
      Business Corporation
      <Sup n={9} />
    </>,
    "complete lines 1–18 (as applicable).",
  ],
];

/** [y, text] — footnotes are 7pt with a hanging indent past the marker. */
const FOOTNOTES: Array<[number, React.ReactNode]> = [
  [
    544,
    <>
      For example, a sole proprietorship or self-employed farmer who establishes
      a qualified retirement plan, or is required to file excise, employment,
      alcohol, tobacco, or
      <br />
      firearms returns, must have an EIN. A partnership, corporation, REMIC
      (real estate mortgage investment conduit), nonprofit organization (church,
      club, etc.), or farmers’
      <br />
      cooperative must use an EIN for any tax-related purpose even if the entity
      doesn’t have employees.
    </>,
  ],
  [
    572,
    <>
      However, don’t apply for a new EIN if the existing entity only (a) changed
      its business name, (b) elected on Form 8832 to change the way it is taxed
      (or is covered by the
      <br />
      default rules), or (c) terminated its partnership status because at least
      50% of the total interests in partnership capital and profits were sold or
      exchanged within a 12-
      <br />
      month period. The EIN of the terminated partnership should continue to be
      used. See Regulations section 301.6109-1(d)(2)(iii).
    </>,
  ],
  [
    601,
    "Don’t use the EIN of the prior business unless you became the “owner” of a corporation by acquiring its stock.",
  ],
  [
    613,
    <>
      However, grantor trusts that don’t file using Optional Method 1 and IRA
      trusts that are required to file Form 990-T, Exempt Organization Business
      Income Tax Return,
      <br />
      must have an EIN. For more information on grantor trusts, see the
      Instructions for Form 1041.
    </>,
  ],
  [
    633,
    "A plan administrator is the person or group of persons specified as the administrator by the instrument under which the plan is operated.",
  ],
  [
    645,
    "Entities applying to be a Qualified Intermediary (QI) need a QI-EIN even if they already have an EIN. See Rev. Proc. 2000-12.",
  ],
  [
    657,
    <>
      See also <i>Household employer agent</i> in the instructions.{" "}
      <b>Note:</b> State or local agencies may need an EIN for other reasons,
      for example, hired employees.
    </>,
  ],
  [
    669,
    <>
      See <i>Disregarded entities</i> in the instructions for details on
      completing Form SS-4 for an LLC.
    </>,
  ],
  [
    681,
    "An existing corporation that is electing or revoking S corporation status should use its previously assigned EIN.",
  ],
];

const SS4Page2 = () => (
  <>
    <Rules
      h={H_RULES_P2}
      v={V_RULES_P2}
      /* the footer rule is the only one heavier than a hairline */
      hWeights={{ 694: 1 }}
    />

    {/* running head */}
    <At x={36} y={37} size={7} fit={81.6}>Form SS-4 (Rev. 12-2025)</At>
    <At x={552} y={37} size={7} fit={16.5}>Page</At>
    <At x={570} y={34} size={10} bold leading={10}>2</At>

    {/* intro, indented past the margin and closed by the rule at y=72 */}
    <At x={68.5} y={51} size={8} leading={8.5}>
      See below to determine whether you need an EIN. However, for further
      information on applying for an EIN, including how to submit
      <br />
      an EIN application, see the separate instructions at www.irs.gov/FormSS4.
    </At>

    <At x={0} y={78} w={612} size={14} bold align="center" leading={15}>
      Do I Need an EIN?
    </At>

    <At x={65} y={99} size={8} leading={8.5}>
      File Form SS-4 if the applicant entity doesn’t already have an EIN but is
      required to show an EIN on any return, statement, or other
      <br />
      document.<Sup n={1} /> See also the separate instructions for each line on
      Form SS-4.
    </At>

    {/* table head, y=120 -> 132 */}
    <At x={C1} y={120} size={8} bold>IF the applicant...</At>
    <At x={C2} y={120} size={8} bold>AND...</At>
    <At x={C3} y={120} size={8} bold>THEN...</At>

    {ROWS.map(([y, a, b, c], i) => (
      <div key={i}>
        {/* The official's own line breaks are carried in ROWS as <br/>, so the
            cells must not wrap on their own — an extra wrap pushes a line out
            of its 12pt row and into the next one. */}
        <At x={C1} y={y} size={8} leading={9}>{a}</At>
        <At x={C2} y={y} size={8} leading={9}>{b}</At>
        <At x={C3} y={y} size={8} leading={9}>{c}</At>
      </div>
    ))}

    {FOOTNOTES.map(([y, body], i) => (
      <div key={`fn${i}`}>
        <At x={36} y={y} size={5}>{i + 1}</At>
        <At x={43} y={y} size={7} leading={8.5}>
          {body}
        </At>
      </div>
    ))}
  </>
);

export default SS4Page2;
